import { useParams } from "react-router-dom";
import { Form, Button, Input, message, Select } from "antd";
import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { apiClient } from "../../../shared/api/AllApi";
import { useAccountantsCatalog } from "../../../hooks/useCatalogData";
import { fetchSystemConfig, readCachedSystemConfig } from "../../../shared/lib/systemConfig";
import { fetchExchangeRates, EXCHANGE_RATES_UPDATED_EVENT } from "../../../shared/lib/exchangeRates";
import { formatCurrency, toApiNumber } from "../../../utils/formatters";

const Payment = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  // const product = Array.isArray(storeCustomer)
  //   ? storeCustomer.find((item) => item.id === parseInt(id))
  //   : null;
  const [amount_paid, setAmount_paid] = useState(0);
  const [amount_main, setAmount_main] = useState(0);
  const [amount_secondary, setAmount_secondary] = useState(0);
  const [exchangeRateId, setExchangeRateId] = useState(null);
  const [systemConfig, setSystemConfig] = useState(() => readCachedSystemConfig());
  const [exchangeRates, setExchangeRates] = useState([]);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [form1] = Form.useForm();
  const [accountant, setAccountant] = useState(null);
  const [personName, setPersonName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false); // Loading state
  const { data: accountants } = useAccountantsCatalog();

  const useDualCurrency = Boolean(systemConfig?.secondary_currency_code);

  const selectedRate = useMemo(() => {
    if (!exchangeRateId || !exchangeRates.length) return null;
    return exchangeRates.find((r) => Number(r.id) === Number(exchangeRateId)) || null;
  }, [exchangeRateId, exchangeRates]);

  const appliedMainTotal = useMemo(() => {
    const main = toApiNumber(amount_main, 0);
    const sec = toApiNumber(amount_secondary, 0);
    if (!useDualCurrency) return toApiNumber(amount_paid, 0);
    const secondaryPerMain = selectedRate ? toApiNumber(selectedRate.main_per_secondary, 0) : 0;
    if (!secondaryPerMain) return Math.round(main * 100) / 100;
    return Math.round((main + sec / secondaryPerMain) * 100) / 100;
  }, [amount_main, amount_secondary, amount_paid, selectedRate, useDualCurrency]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = await fetchSystemConfig();
        if (cancelled) return;
        setSystemConfig(cfg);
        if (!cfg?.secondary_currency_code) {
          setExchangeRates([]);
          setExchangeRateId(null);
          return;
        }
        const rates = await fetchExchangeRates();
        if (cancelled) return;
        setExchangeRates(rates);
        setExchangeRateId((prev) =>
          prev && rates.some((r) => Number(r.id) === Number(prev)) ? prev : rates[0]?.id ?? null
        );
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onFxUpdated = () => {
      (async () => {
        try {
          const cfg = await fetchSystemConfig();
          setSystemConfig(cfg);
          if (!cfg?.secondary_currency_code) {
            setExchangeRates([]);
            setExchangeRateId(null);
            return;
          }
          const rates = await fetchExchangeRates();
          setExchangeRates(rates);
          setExchangeRateId((prev) =>
            prev && rates.some((r) => Number(r.id) === Number(prev)) ? prev : rates[0]?.id ?? null
          );
        } catch {
          /* ignore */
        }
      })();
    };
    window.addEventListener(EXCHANGE_RATES_UPDATED_EVENT, onFxUpdated);
    return () => window.removeEventListener(EXCHANGE_RATES_UPDATED_EVENT, onFxUpdated);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get(`/customers/${id}`);
        if (cancelled) return;
        setCustomerInfo({
          name: res?.data?.name || "",
          total_owed: toApiNumber(res?.data?.total_owed, 0),
        });
      } catch {
        if (!cancelled) setCustomerInfo(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Function to handle form submission
  const PostApi = async () => {
    if (isSubmitting) return; // Prevent double submission
    setIsSubmitting(true);

    try {
      const secVal = Math.round(toApiNumber(amount_secondary, 0) * 100) / 100;
      if (useDualCurrency && secVal > 0 && !exchangeRateId) {
        message.error(t("Please select an exchange rate."));
        return;
      }
      let formData = new FormData();
      formData.append("id", id); // Ensure ID is included
      if (useDualCurrency) {
        formData.append("amount_main", String(Math.round(toApiNumber(amount_main, 0) * 100) / 100));
        formData.append("amount_secondary", String(secVal));
        if (secVal > 0 && exchangeRateId) {
          formData.append("exchange_rate_id", String(exchangeRateId));
        }
      } else {
        formData.append("amount", amount_paid);
      }
      formData.append("receiver", accountant); // accountant as receiver
      formData.append("sender", personName);   // personName as sender
      formData.append("payment_method", "cash"); // default to cash
      for (const pair of formData.entries()) {
        console.log(pair[0], pair[1]);
      }
      const res = await apiClient.post(`/customers/pay/${id}`, formData);
      form1.resetFields(); // Reset form on success
      // Get payment id from response
      const paymentId = res.data && res.data.payment ? res.data.payment.id : null;
      // Redirect to PaymentRecipt and pass customer id and payment id as params
      if (paymentId) {
        window.location.href = `/GetCustomer/PaymentRecipt/${id}/${paymentId}`;
      } else {
        window.location.href = `/GetCustomer/PaymentRecipt/${id}`;
      }
    } catch (error) {
      console.error("Error:", error);
      message.error(error?.response?.data?.message || "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      style={{
        textAlign: "center",
        marginTop: "50px",
        direction: "rtl", // Right-to-left layout for Arabic and Kurdish
      }}
    >
      <h1 style={{ fontSize: "24px", marginBottom: "20px" }}>
        {t("amountPaid")} {/* Translated heading */}
      </h1>
      {customerInfo && (
        <div
          style={{
            margin: "0 auto 20px auto",
            width: "70%",
            border: "1px solid #91d5ff",
            background: "#e6f7ff",
            borderRadius: 8,
            padding: "10px 14px",
            fontSize: "16px",
            fontWeight: 600,
          }}
        >
          {customerInfo.name ? `${customerInfo.name} — ` : ""}
          {t("totalOwed")}: {formatCurrency(customerInfo.total_owed, systemConfig?.currency_code)}
        </div>
      )}
      <Form
        form={form1}
        onFinish={PostApi} // Trigger API on form submit
        style={{ fontSize: "18px" }}
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 16 }}
      >
        {useDualCurrency ? (
          <>
            <Form.Item
              label={
                <span style={{ fontSize: "18px", fontWeight: "bold" }}>
                  {t("amountPaid")} ({systemConfig?.currency_code})
                </span>
              }
            >
              <Input
                style={{ width: "70%", fontSize: "16px", textAlign: "center" }}
                type="number"
                min={0}
                value={amount_main}
                onChange={(e) => setAmount_main(e.target.value)}
              />
            </Form.Item>
            <Form.Item
              label={
                <span style={{ fontSize: "18px", fontWeight: "bold" }}>
                  {t("amountPaid")} ({systemConfig?.secondary_currency_code})
                </span>
              }
            >
              <Input
                style={{ width: "70%", fontSize: "16px", textAlign: "center" }}
                type="number"
                min={0}
                value={amount_secondary}
                onChange={(e) => setAmount_secondary(e.target.value)}
              />
            </Form.Item>
            {exchangeRates.length > 0 && (
              <Form.Item label={<span style={{ fontSize: "18px", fontWeight: "bold" }}>{t("Exchange rate")}</span>}>
                <Select
                  style={{ width: "70%", fontSize: "16px" }}
                  placeholder={t("Select rate")}
                  value={exchangeRateId}
                  onChange={setExchangeRateId}
                >
                  {exchangeRates.map((r) => (
                    <Select.Option key={r.id} value={r.id}>
                      {String(r.effective_from || "").slice(0, 10)} — 1 {systemConfig?.currency_code} = {r.main_per_secondary}{" "}
                      {systemConfig?.secondary_currency_code}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            )}
            <div style={{ marginBottom: 16, fontSize: "16px" }}>
              {t("Total applied")} ({systemConfig?.currency_code}): <strong>{formatCurrency(appliedMainTotal, systemConfig?.currency_code)}</strong>
            </div>
          </>
        ) : (
          <Form.Item
            name="amount_paid"
            label={
              <span style={{ fontSize: "18px", fontWeight: "bold" }}>
                {t("amountPaid")}
              </span>
            }
          >
            <Input
              style={{ width: "70%", fontSize: "16px", textAlign: "center" }}
              value={amount_paid}
              onChange={(e) => setAmount_paid(e.target.value)}
            />
          </Form.Item>
        )}
        <Form.Item
          name="accountant"
          label={
            <span style={{ fontSize: "18px", fontWeight: "bold" }}>
              {t("accountant")}
            </span>
          }
          rules={[{ required: true, message: t("Please select accountant") }]}
        >
          <Select
            showSearch
            placeholder={t("Select accountant")}
            value={accountant}
            onChange={setAccountant}
            style={{ width: "70%", fontSize: "16px" }}
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {accountants.map((acc) => (
              <Select.Option key={acc.id} value={acc.name}>
                {acc.name} {acc.email ? `(${acc.email})` : ""}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="person_name"
          label={
            <span style={{ fontSize: "18px", fontWeight: "bold" }}>
              {t("personName")}
            </span>
          }
        >
          <Input
            style={{ width: "70%", fontSize: "16px" }}
            value={personName}
            onChange={(e) => setPersonName(e.target.value)}
            placeholder={t("Enter person name")}
          />
        </Form.Item>

        <Form.Item label=".">
          <Button
            type="primary"
            htmlType="submit"
            loading={isSubmitting}
            disabled={isSubmitting}
            style={{
              backgroundColor: "green",
              borderColor: "green",
              fontSize: "16px",
            }}
          >
            {t("submit")}
          </Button>
          <span style={{ margin: "0 8px" }}></span>
          <Button
            type="primary"
            danger
            onClick={() => form1.resetFields()}
            style={{
              backgroundColor: "blue",
              borderColor: "blue",
              fontSize: "16px",
            }}
          >
            {t("reset")} {/* Translated button text */}
          </Button>
          <span style={{ margin: "0 8px" }}></span>
          <Button
            type="primary"
            danger
            onClick={() => (window.location.href = "/GetCustomer")}
            style={{
              backgroundColor: "red",
              borderColor: "red",
              fontSize: "16px",
            }}
          >
            {t("cancel")} {/* Translated button text */}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default Payment;

